import { useEffect, useId, useRef } from 'react';
import type { ReactNode } from 'react';
import styles from './bottom-sheet.module.css';

type BottomSheetProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  label?: string;
  closeLabel?: string;
};

export const BottomSheet = ({
  open,
  onClose,
  children,
  title,
  label,
  closeLabel = 'Скасувати',
}: BottomSheetProps) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      panelRef.current?.focus();
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const hasTitle = title !== undefined;

  return (
    <div className={styles.layer}>
      <button type="button" className={styles.backdrop} aria-label="Закрити" onClick={onClose} />
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasTitle ? titleId : undefined}
        aria-label={hasTitle ? undefined : label}
        tabIndex={-1}
      >
        {hasTitle && (
          <p id={titleId} className={styles.title}>
            {title}
          </p>
        )}
        {children}
        <button type="button" className={styles.close} onClick={onClose}>
          {closeLabel}
        </button>
      </div>
    </div>
  );
};
